import XLSX from 'xlsx-js-style';
import { CUSTOM_FUND_NOTE, CURRENT_WEIGHTS_NOTE } from './utils/allocationMath';

// Excel export mirroring the on-screen allocation table. Styling follows the
// Portfolio Viewer export (navy header, grey sector rows, gold total row).
// In comparison mode the sheet carries Current % / $, Target % / $ and the
// signed Change % / $ columns, plus the adviser-input note.
export function exportAllocationToExcel({ allocation, total, blendSummary, disclaimer }) {
  const { retainedRows, sectors, totalRow, exposure, comparison } = allocation;

  const headerStyle = {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '072040' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: { bottom: { style: 'thin', color: { rgb: '003366' } } },
  };
  const sectorStyle = {
    font: { bold: true, sz: 10, color: { rgb: '072040' } },
    fill: { fgColor: { rgb: 'E7E6E6' } },
    border: {
      bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
      top: { style: 'thin', color: { rgb: 'CCCCCC' } },
    },
  };
  const cellStyle = {
    font: { sz: 10 },
    border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
  };
  const totalStyle = {
    font: { bold: true, sz: 10, color: { rgb: '072040' } },
    fill: { fgColor: { rgb: 'F0A500' } },
    border: {
      top: { style: 'medium', color: { rgb: '072040' } },
      bottom: { style: 'medium', color: { rgb: '072040' } },
    },
  };
  const pctFmt = '0.00"%"';
  const dollarFmt = '"$"#,##0';
  const signedPctFmt = '+0.00"%";-0.00"%";0.00"%"';
  const signedDollarFmt = '+"$"#,##0;-"$"#,##0;"$"0';
  const num = (base, fmt) => ({ ...base, alignment: { horizontal: 'right' }, numFmt: fmt });

  // Column layout: [name, code, ...figures]. Figure columns carry a number
  // format each; blank ('') cells in a figure column stay unformatted text.
  const figureCols = comparison
    ? [
      ['Current %', pctFmt], ['Current $', dollarFmt],
      ['Target %', pctFmt], ['Target $', dollarFmt],
      ['Change %', signedPctFmt], ['Change $', signedDollarFmt],
    ]
    : [['%', pctFmt], ['$', dollarFmt]];
  const ncols = 2 + figureCols.length;

  const figures = (f) => (comparison
    ? [f.curPct, f.curDollars, f.pct, f.dollars, f.changePct, f.changeDollars]
    : [f.pct, f.dollars]);

  const aoa = [['Asset Class / Investment Option', 'ASX / APIR Code', ...figureCols.map(([h]) => h)]];
  const rowMeta = ['header'];

  const pushRow = (name, code, figs, type) => {
    aoa.push([name, code, ...figs]);
    rowMeta.push(type);
  };

  (retainedRows || []).forEach((r) => {
    pushRow(r.name, '', figures(r), 'retained');
  });
  sectors.forEach((sector) => {
    pushRow(sector.label, '', figures({
      pct: sector.subtotalPct, dollars: sector.subtotalDollars,
      curPct: sector.subtotalCurPct, curDollars: sector.subtotalCurDollars,
      changePct: sector.changePct, changeDollars: sector.changeDollars,
    }), 'sector');
    sector.rows.forEach((r) => pushRow(
      r.isCustom ? `${r.name} *` : r.name, r.code || '', figures(r), 'data',
    ));
  });
  pushRow('Total', '', figures(totalRow), 'total');
  if (exposure) {
    const expo = (label, pick) => pushRow(label, '', figures({
      pct: pick(exposure).pct, dollars: pick(exposure).dollars,
      curPct: exposure.current ? pick(exposure.current).pct : '',
      curDollars: exposure.current ? pick(exposure.current).dollars : '',
      changePct: exposure.current ? Math.round((pick(exposure).pct - pick(exposure.current).pct) * 100) / 100 : '',
      changeDollars: exposure.current ? pick(exposure).dollars - pick(exposure.current).dollars : '',
    }), 'sector');
    expo('Growth assets', (e) => ({ pct: e.growthPct, dollars: e.growthDollars }));
    expo('Defensive assets', (e) => ({ pct: e.defensivePct, dollars: e.defensiveDollars }));
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 45 }, { wch: 16 }, ...figureCols.map(([h]) => ({ wch: h.endsWith('$') ? 14 : 11 }))];

  for (let r = 0; r < aoa.length; r++) {
    const type = rowMeta[r];
    for (let c = 0; c < ncols; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      let base;
      if (type === 'header') base = headerStyle;
      else if (type === 'sector') base = sectorStyle;
      else if (type === 'total') base = totalStyle;
      else if (type === 'retained') base = sectorStyle;
      else base = cellStyle;
      if (c >= 2) ws[ref].s = num(base, figureCols[c - 2][1]);
      else ws[ref].s = base;
    }
  }

  // Footer: allocation basis, any footnotes, then the approved disclaimer
  // (merged across the table width and wrapped so the full text is visible).
  const footnoteStyle = { font: { sz: 9, italic: true, color: { rgb: '6C757D' } } };
  let row = aoa.length + 1;
  const footnote = (text) => {
    XLSX.utils.sheet_add_aoa(ws, [[text]], { origin: { r: row, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = footnoteStyle;
    row += 1;
  };
  footnote(`Allocation basis: ${blendSummary}`);
  const hasCustom = sectors.some((s) => s.rows.some((r) => r.isCustom));
  if (hasCustom) footnote(CUSTOM_FUND_NOTE);
  if (comparison) footnote(CURRENT_WEIGHTS_NOTE);

  const disclaimerRow = row + 1;
  XLSX.utils.sheet_add_aoa(ws, [[disclaimer]],
    { origin: { r: disclaimerRow, c: 0 } });
  ws[XLSX.utils.encode_cell({ r: disclaimerRow, c: 0 })].s = {
    ...footnoteStyle,
    alignment: { wrapText: true, vertical: 'top' },
  };
  ws['!merges'] = [
    ...(ws['!merges'] || []),
    { s: { r: disclaimerRow, c: 0 }, e: { r: disclaimerRow, c: ncols - 1 } },
  ];
  ws['!rows'] = ws['!rows'] || [];
  ws['!rows'][disclaimerRow] = { hpt: 110 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Allocation');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Allocation_${total.toLocaleString('en-AU').replace(/,/g, '')}_${stamp}.xlsx`);
}
